/**
 * Structural validation for RAW parser output (Path A — manual/batch upload +
 * retailer custom prompts, e.g. docs/ea-parsing-prompts.md). This is the
 * snake_case shape the common/retailer-custom prompts emit directly from the
 * LLM (contract_terms, smooth.environmental_charges, smooth.non_energy_charges) —
 * distinct from the camelCase ParsedOfferData/Zod schema in parsedOfferSchema.ts,
 * which validates the ALREADY-NORMALISED shape used by the email ingestion path.
 *
 * Previously `/parsing/parse` returned whatever JSON the LLM produced with zero
 * validation — a term with missing/non-numeric rates silently became a blank or
 * $0 line item in the UI with no indication anything was wrong. This module adds
 * a cheap, non-blocking structural check so that failure is visible instead of silent.
 */

import { detectSplitAgreementYears, type ContractTermsMap } from '../lib/steppedAgreementTerms';

export type ParsedOfferValidation = {
  /** false only when there is NOT ONE usable contract term — nothing to calculate from */
  valid: boolean;
  /** Human-readable issues found, most severe first. Non-fatal issues do not flip `valid` to false. */
  warnings: string[];
};

function isFiniteNumberOrNull(v: unknown): boolean {
  return v === null || v === undefined || (typeof v === 'number' && Number.isFinite(v));
}

/**
 * How often each non-energy charge appears across a retailer's other offers.
 * Built from the database by the caller (see buildRetailerChargeProfile) — this module
 * stays pure and takes it as an argument.
 */
export type RetailerChargeProfile = {
  /** Offers for this retailer that carry ANY non-energy charge data — the denominator. */
  offersWithData: number;
  /** snake_case field name → how many of those offers state it. */
  presentCount: Record<string, number>;
};

/** The non-energy fields worth cross-checking, and how to name them to a human. */
const NON_ENERGY_FIELDS: { snake: string; camel: string; label: string }[] = [
  { snake: 'meter_agreement',        camel: 'meterAgreement',        label: 'Meter Agreement' },
  { snake: 'service_charge',         camel: 'serviceCharge',         label: 'Service Charge' },
  { snake: 'supplementary_metering', camel: 'supplementaryMetering', label: 'AEMO Participant / Supplementary Metering' },
  { snake: 'market_fees',            camel: 'marketFees',            label: 'Market Fees' },
];

/**
 * A retailer must state a charge on at least this share of its offers before its absence
 * on one offer counts as evidence of a miss rather than a genuine "they don't charge it".
 * AGL states Service Charge on 29 of 34 (85%) and AEMO Participant on 30 of 34 — so the
 * four offers missing both stand out. Shell states Service Charge on 1 of 13 (8%), Engie
 * states Supplementary Metering on 0 of 22, and neither should ever be flagged.
 */
const CHARGE_EXPECTED_SHARE = 0.6;

/** Below this many offers there is not enough history to call anything unusual. */
const MIN_PROFILE_OFFERS = 3;

/**
 * Charges this retailer normally states that this offer does not.
 *
 * Not a correction — the value cannot be invented, only the gap pointed at. A real case:
 * an AGL offer parsed with Meter Agreement $3,996 but null Service Charge and null AEMO
 * Participant, where every other AGL offer carries $607 and $175. Other Charges came out
 * $3,996 instead of $4,778, understating that offer by $782 a year — and because the
 * understatement makes an offer look cheaper, it can move it up the ranking. Nothing in
 * the UI said a word: null reads exactly like "this retailer does not charge it".
 *
 * Returns [] when there is no profile, too little history, or the offer states nothing at
 * all (that case is already covered by the "No non-energy charges found" warning).
 */
export function detectMissingNonEnergyCharges(
  nonEnergyCharges: unknown,
  profile: RetailerChargeProfile | null | undefined,
  retailerName?: string,
): string[] {
  if (!profile || profile.offersWithData < MIN_PROFILE_OFFERS) return [];
  if (!nonEnergyCharges || typeof nonEnergyCharges !== 'object') return [];

  const charges = nonEnergyCharges as Record<string, unknown>;
  const valueOf = (f: { snake: string; camel: string }): unknown =>
    charges[f.snake] ?? charges[f.camel];
  const isStated = (v: unknown): boolean => typeof v === 'number' && Number.isFinite(v) && v !== 0;

  // An offer stating nothing is a different (already-reported) problem; only a PARTIALLY
  // filled block means the parser read this document and dropped some of its lines.
  if (!NON_ENERGY_FIELDS.some(f => isStated(valueOf(f)))) return [];

  const who = retailerName ? `${retailerName} ` : 'This retailer ';
  const warnings: string[] = [];

  for (const field of NON_ENERGY_FIELDS) {
    if (isStated(valueOf(field))) continue;
    const present = profile.presentCount[field.snake] ?? profile.presentCount[field.camel] ?? 0;
    const share = present / profile.offersWithData;
    if (share < CHARGE_EXPECTED_SHARE) continue;

    warnings.push(
      `${field.label} is missing, but ${who}states it on ${present} of ${profile.offersWithData}`
      + ` other offers — check the document and enter it manually if it is there.`
      + ` Leaving it out understates this offer.`,
    );
  }

  return warnings;
}

/** Does this year_N rate object have usable peak/offpeak numbers? */
function isUsableYearRate(year: any): boolean {
  if (!year || typeof year !== 'object') return false;
  return typeof year.peak === 'number' && Number.isFinite(year.peak)
    && typeof year.offpeak === 'number' && Number.isFinite(year.offpeak);
}

/** The year_N slot numbers a term carries, ascending. */
function yearNumbersOf(term: Record<string, any>): number[] {
  return Object.keys(term)
    .map(k => k.match(/^year_(\d+)$/)?.[1])
    .filter((n): n is string => !!n)
    .map(Number)
    .sort((a, b) => a - b);
}

/**
 * Stepped terms carrying a year slot with no rates in it.
 *
 * A stepped term is billed year by year, so calculateSteppedContract cannot price one whose
 * year_2 is an empty object — the whole term is dropped and the retailer silently disappears
 * from that year's comparison. Origin's NYM NOOSA offer lost 24/36/48 exactly this way and
 * ranked in Year 1 only; nothing in the UI said so.
 *
 * Smooth terms are deliberately NOT flagged: they are priced from a single year slot
 * (smoothRateYear), so a later empty year costs nothing and warning about it is noise.
 */
export function detectUnpricedSteppedYears(contractTerms: unknown): string[] {
  if (!contractTerms || typeof contractTerms !== 'object') return [];
  const warnings: string[] = [];

  for (const [key, termData] of Object.entries(contractTerms as Record<string, any>)) {
    const term = termData as Record<string, any>;
    if (!term || typeof term !== 'object') continue;
    if (term.rate_type !== 'Stepped') continue;

    const years = yearNumbersOf(term);
    if (years.length < 2) continue;

    const unpriced = years.filter(n => !isUsableYearRate(term[`year_${n}`]));
    if (unpriced.length === 0) continue;

    warnings.push(
      `Term "${key}": stepped, but year ${unpriced.join(', ')} of ${years.length}`
      + ` carr${unpriced.length === 1 ? 'ies' : 'y'} no rates.`
      + ` A stepped term is billed year by year, so this term cannot be costed and will be`
      + ` dropped from the comparison entirely.`,
    );
  }

  return warnings;
}

/**
 * One agreement's YEARS mis-read as several separate term OPTIONS.
 *
 * Origin's schedule lists "Agreement Year 1..5" of a single 51-month contract. The parser
 * emitted four terms — 12/24/36/48 — each holding one agreement year's rates in its own
 * year_1 with every later slot left as `{}`, and dropped Agreement Year 5 altogether. The
 * offer should have been ONE 51-month stepped term with year_1..year_5.
 *
 * The decisive signal is available_terms, which the parser got right: it named "51" while no
 * contract_terms key matched. Rate values alone cannot separate the two shapes, because a
 * genuine set of term options also quotes a different rate per option — so this only fires
 * when the document's own declared term length is missing from the keys.
 *
 * Deliberately narrow. Every one of these must hold: two or more terms, all stepped, each
 * pricing only its year_1 while carrying later empty slots, all those year_1 rates distinct,
 * and exactly one declared term length absent from the keys.
 */
export function detectAgreementYearsSplitAcrossTerms(
  contractTerms: unknown,
  availableTerms: unknown,
): string[] {
  // Detection lives in steppedAgreementTerms so the warning below and the repair the
  // calculation applies can never disagree about what this shape is.
  const split = detectSplitAgreementYears(contractTerms as ContractTermsMap, availableTerms);
  if (!split) return [];

  const keys = Object.keys(contractTerms as Record<string, any>).map(k => `"${k}"`).join(', ');
  const recovered = split.yearsInOrder.length;
  return [
    `available_terms names a ${split.declaredMonths}-month term, but contract_terms instead`
    + ` holds ${recovered} terms (${keys}) that each price only year_1 and leave every later`
    + ` year empty. This is one ${split.declaredMonths}-month stepped agreement whose`
    + ` AGREEMENT YEARS were split into separate terms — it must be a single`
    + ` "${split.declaredMonths}" term with year_1 onward. The calculation rebuilds the`
    + ` ${recovered} years it can recover so the offer is still costed, but any agreement year`
    + ` beyond the ${recovered}th was never parsed and cannot be restored from stored data.`
    + ` RE-PARSE this document.`,
  ];
}

/**
 * Validate the raw snake_case parser output. Never throws — always returns a result.
 */
/**
 * Environmental year slots that do not begin at `year_1`, per scheme.
 *
 * ⚠️ A MISSING FIRST PERIOD CANNOT BE RECOVERED LATER — it has to be caught at parse time.
 *
 * Every retailer's environmental table opens with a partial period running from the supply
 * start date to 31 December of that year, and both the Flo and Next Business prompts spell
 * that out ("year_1 = CAL26 period: supply start date -> 31/12/2026 (partial first year)").
 * Two prod parses ignored it anyway and began at `year_2`:
 *
 *   WERRIBEE RACING CLUB (tender bd3f75f1), verified against the source PDFs
 *     Flo      lrec/sres/veec = year_2..year_4   -- 01/09/2026-31/12/2026 row absent
 *     NextBus  lrec/sres/veec = year_2..year_3   -- CAL26 column absent
 *     AGL / Origin / Momentum = year_1..year_N   -- correct, same partial first period
 *
 * The year LABELS were right (Flo's year_2 really is the 2027 row), so nothing downstream
 * can tell the data is short — the calculation simply prices the term off later, more
 * expensive years. Flo's 28-month environmental came out $5,641 against $3,899, and the
 * 16-month figure was double. The offer has to be re-parsed; the point of this check is that
 * the reviewer SEES it in Stage 3 instead of it shipping silently.
 */
function detectEnvironmentalFirstYearGaps(data: Record<string, any>): string[] {
  const env = data?.smooth?.environmental_charges
    ?? data?.smooth?.environmentalCharges
    ?? data?.environmental_charges
    ?? data?.stepped?.environmental_charges
    ?? data?.stepped?.environmentalCharges;
  if (!env || typeof env !== 'object') return [];

  const gaps: string[] = [];
  for (const [scheme, years] of Object.entries(env as Record<string, any>)) {
    if (!years || typeof years !== 'object') continue;

    const slots = Object.keys(years)
      .map(k => /^year_(\d+)$/.exec(k)?.[1])
      .filter((n): n is string => !!n)
      .map(Number)
      .sort((a, b) => a - b);
    if (slots.length === 0) continue;

    if (slots[0] !== 1) {
      gaps.push(`Environmental "${scheme}": starts at year_${slots[0]}, not year_1`
        + ` — the first period (supply start to 31 Dec) was not captured.`
        + ` This CANNOT be corrected after parsing and will price the term off later years.`
        + ` Re-parse this document.`);
      continue;
    }

    // A hole in the middle is the same class of fault and equally unrecoverable.
    const missing = [];
    for (let n = 1; n <= slots[slots.length - 1]; n++) if (!slots.includes(n)) missing.push(n);
    if (missing.length > 0) {
      gaps.push(`Environmental "${scheme}": year_${missing.join(', year_')} missing`
        + ` between year_1 and year_${slots[slots.length - 1]} — re-parse this document.`);
    }
  }
  return gaps;
}

export function validateParsedOfferSnakeCase(
  json: unknown,
  /**
   * Optional cross-offer context. Without it the structural checks below run exactly as
   * before; with it, a charge this retailer normally states but this document did not is
   * reported too (see detectMissingNonEnergyCharges).
   */
  options?: { retailerChargeProfile?: RetailerChargeProfile | null; retailerName?: string },
): ParsedOfferValidation {
  const warnings: string[] = [];

  if (!json || typeof json !== 'object') {
    return { valid: false, warnings: ['Parsed output is not a JSON object'] };
  }

  const data = json as Record<string, any>;
  const contractTerms = data.contract_terms;

  // Reported first: an unrecoverable data gap outranks any rate-shape nit below it.
  warnings.push(...detectEnvironmentalFirstYearGaps(data));

  if (!contractTerms || typeof contractTerms !== 'object' || Object.keys(contractTerms).length === 0) {
    return { valid: false, warnings: ['No contract_terms found — nothing to calculate from'] };
  }

  let usableTermCount = 0;

  for (const [key, termData] of Object.entries(contractTerms)) {
    const term = termData as Record<string, any>;
    if (!term || typeof term !== 'object') {
      warnings.push(`Term "${key}": not an object`);
      continue;
    }

    if (term.rate_type && term.rate_type !== 'Smooth' && term.rate_type !== 'Stepped') {
      warnings.push(`Term "${key}": rate_type "${term.rate_type}" is neither "Smooth" nor "Stepped"`);
    }

    const yearKeys = Object.keys(term).filter(k => /^year_\d+$/.test(k));
    if (yearKeys.length === 0) {
      warnings.push(`Term "${key}": no year_N rate data`);
      continue;
    }

    const hasUsableYear = yearKeys.some(yk => isUsableYearRate(term[yk]));
    if (!hasUsableYear) {
      warnings.push(`Term "${key}": no year has both a numeric peak and offpeak rate`);
      continue;
    }

    // Shoulder must be a number or null when present — a string or NaN silently breaks calc.
    for (const yk of yearKeys) {
      const shoulder = term[yk]?.shoulder;
      if (shoulder !== undefined && !isFiniteNumberOrNull(shoulder)) {
        warnings.push(`Term "${key}" ${yk}: shoulder rate "${shoulder}" is not a number or null`);
      }
    }

    usableTermCount += 1;
  }

  if (usableTermCount === 0) {
    return { valid: false, warnings: [...warnings, 'Every contract term is missing usable rate data'] };
  }

  // A term that looks usable above can still be uncostable: `hasUsableYear` passes on year_1
  // alone, which is exactly the shape that silently drops a stepped term. Reported ahead of
  // the charge warnings below because losing a whole term outranks a missing fee.
  warnings.push(...detectAgreementYearsSplitAcrossTerms(contractTerms, data.available_terms));
  warnings.push(...detectUnpricedSteppedYears(contractTerms));

  // Non-fatal: environmental / non-energy charges commonly live under smooth.*
  const envCharges = data.environmental_charges ?? data.smooth?.environmental_charges;
  if (!envCharges || typeof envCharges !== 'object' || Object.keys(envCharges).length === 0) {
    warnings.push('No environmental charges found (LREC/SREC/ESC/VEEC) — will calculate as $0');
  }

  const nonEnergy = data.non_energy_charges ?? data.smooth?.non_energy_charges;
  if (!nonEnergy || typeof nonEnergy !== 'object' || Object.keys(nonEnergy).length === 0) {
    warnings.push('No non-energy charges found (supply/metering fees) — will calculate as $0');
  } else {
    // A block that exists but has null fields passes every check above while quietly
    // dropping real money. Only a retailer's own history can tell "they don't charge it"
    // apart from "we failed to read it", so this runs only when that history was supplied.
    warnings.push(...detectMissingNonEnergyCharges(
      nonEnergy,
      options?.retailerChargeProfile,
      options?.retailerName,
    ));
  }

  return { valid: true, warnings };
}
